import {
  WikiArticle,
  WikiArticleMain,
  WikiArticleSubPanel,
} from "../WikiArticle";
import {
  LinkToSection,
  SectionIndex,
  SectionTitle,
} from "../../../new_component/organisms/SectionIndex";
import { Heading, ListItem, Text, UnorderedList, Box, Image, Link } from "@chakra-ui/react";
import CodeExample from "../../../new_component/organisms/CodeExample";

const Applications = () => (
  <WikiArticle>
    <WikiArticleMain>
      <section>
        <Heading as="h2">Applications</Heading>
        <Text>
          Applications in FQL are templates that provide users with a predefined
          structure of forms and database schemas. They enable fast onboarding,
          form replication, and database schema provisioning per user or guest,
          based on an <code>application_id</code>.
        </Text>
      </section>

      <section>
        <SectionTitle>Core Concepts</SectionTitle>
        <UnorderedList>
          <ListItem>
            <Text as="b">Templates: </Text>Each application contains a set of
            FQL commands that define the structure of the forms users will
            receive upon registration.
          </ListItem>
          <ListItem>
            <Text as="b">Pool Size: </Text>Applications use a{" "}
            <code>pool_size</code> to pre-create a number of database schemas,
            improving performance during user registration.
          </ListItem>
          <ListItem>
            <Text as="b">Automatic Provisioning: </Text>Whenever a new user or
            guest registers using an <code>application_id</code>, one of these
            pre-created schemas is assigned. A new schema is generated when the
            pool runs low.
          </ListItem>
        </UnorderedList>
      </section>

      <section>
        <SectionTitle>API: Create Application</SectionTitle>
        <Text>
          To create an application, send a POST request to the following
          endpoint:
        </Text>
        <CodeExample title="Endpoint" run={false}>
          {`POST /api/create_application`}
        </CodeExample>
        <Text>
          <b>Headers:</b>
        </Text>
        <CodeExample title="Headers" run={false}>
          {"Authorization: Bearer <user_token>"}
        </CodeExample>
        <Text>
          <b>Body:</b>
        </Text>
        <CodeExample title="JSON" run={false} multiline>
          {`{
  "name": "Client Intake App",
  "commands": ["CREATE FORM CLIENT(...);", "..."],
  "pool_size": 5
}`}
        </CodeExample>
        <Text>
          <b>Response:</b>
        </Text>
        <CodeExample title="JSON" run={false}>
          {`{
  "application_id": "app_abc123xyz"
}`}
        </CodeExample>
      </section>

      <section>
        <SectionTitle>Registering Users or Guests</SectionTitle>
        <Text>
          Once you have an <code>application_id</code>, you can use it to
          automatically assign users or guests a fully-prepared environment.
        </Text>

        <Text>
          <b>Register a Guest:</b>
        </Text>
        <CodeExample title="Endpoint" run={false}>
          {"POST /api/auth/register/guest"}
        </CodeExample>
        <CodeExample title="JSON Body" run={false}>
          {`{
  "application_id": "app_abc123xyz"
}`}
        </CodeExample>

        <Text>
          <b>Register a User:</b>
        </Text>
        <CodeExample title="Endpoint" run={false}>
          {"POST /api/auth/register"}
        </CodeExample>
        <CodeExample title="JSON Body" run={false}>
          {`{
  "email": "john@doe.com",
  "application_id": "app_abc123xyz"
}`}
        </CodeExample>

        <Text>
          In both cases, the system automatically assigns a schema from the pool
          and initializes it with the forms defined in the application.
        </Text>
      </section>

      <section>
        <SectionTitle>Alternative: Create via UI</SectionTitle>
        <Text>
          If you're not using the API directly, you can also <Link href="/create_app" color="teal.500">create an application</Link> via the user interface:
        </Text>
        
        <Box mt={4} border="1px solid" borderColor="gray.200" p={2} borderRadius="md">
          <Image
            src="/images/create-app-ui-placeholder.png"
            alt="Create App UI"
            objectFit="contain"
            w="100%"
            h="auto"
          />
        </Box>
        <Text mt={2}>
          This UI allows you to define the name, pool size, and FQL commands in a
          user-friendly form. The resulting application behaves exactly like
          those created via API.
        </Text>
      </section>
    </WikiArticleMain>

    <WikiArticleSubPanel>
      <SectionIndex>
        <ListItem>
          <LinkToSection>Applications</LinkToSection>
        </ListItem>
        <ListItem>
          <LinkToSection>Core Concepts</LinkToSection>
        </ListItem>
        <ListItem>
          <LinkToSection>API: Create Application</LinkToSection>
        </ListItem>
        <ListItem>
          <LinkToSection>Registering Users or Guests</LinkToSection>
        </ListItem>
        <ListItem>
          <LinkToSection>Alternative: Create via UI</LinkToSection>
        </ListItem>
      </SectionIndex>
    </WikiArticleSubPanel>
  </WikiArticle>
);

export default Applications;
